// Request/response + notification layer over the media-signaling WebSocket
// (docs/design/10-signaling-protocol.md §3). Envelope: { id, method, data } out;
// { id, ok, data } | { id, error } in; { notification, data } pushed.

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

export type NotificationHandler = (notification: string, data: Record<string, unknown>) => void;

export class SignalingSocket {
  private ws?: WebSocket;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();

  constructor(
    private readonly url: string,
    private readonly onNotification: NotificationHandler
  ) {}

  open(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      let opened = false;
      ws.onopen = () => { opened = true; resolve(); };
      ws.onerror = () => reject(new Error("media signaling socket error"));
      ws.onmessage = (ev) => this.onMessage(ev);
      ws.onclose = () => {
        // Reject the open() promise if the socket closed before onopen fired
        // (e.g. stop() called during connect). Without this, open() hangs forever.
        if (!opened) reject(new Error("socket closed"));
        for (const [, p] of this.pending) p.reject(new Error("socket closed"));
        this.pending.clear();
      };
    });
  }

  private onMessage(ev: MessageEvent): void {
    let msg: { id?: number; ok?: boolean; data?: unknown; error?: { code: string; message: string }; notification?: string };
    try {
      msg = JSON.parse(typeof ev.data === "string" ? ev.data : "");
    } catch {
      return;
    }
    if (msg.notification) {
      this.onNotification(msg.notification, (msg.data as Record<string, unknown>) ?? {});
      return;
    }
    if (typeof msg.id === "number") {
      const pending = this.pending.get(msg.id);
      if (!pending) return;
      this.pending.delete(msg.id);
      if (msg.error) pending.reject(new Error(`${msg.error.code}: ${msg.error.message}`));
      else pending.resolve(msg.data);
    }
  }

  request<T = unknown>(method: string, data: Record<string, unknown> = {}): Promise<T> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("signaling socket not open"));
    }
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      ws.send(JSON.stringify({ id, method, data }));
    });
  }

  close(): void {
    this.ws?.close();
    this.ws = undefined;
  }
}
